import { useEffect, useState } from "react";
import axios from "axios";

import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import ManagerHeader from "./managerheader";
import KPICards from "./KPICards";
import ChartsSection from "./ChartsSection";
import ArrivalsDepartures from "./ArrivalsDepartures";
import RoomStatusBar from "./RoomStatusBar";
import StaffShifts from "./StaffShifts";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

export default function ManagerDashboard() {
  const [loading, setLoading] = useState(true);

  // ==========================
  //  AUTH HEADERS (TOKEN ONLY)
  // ==========================
  const token = localStorage.getItem("ai2_token");

  const authHeaders = {
    Authorization: `Bearer ${token}`,   // ⭐ ONLY THIS — NO x-hotel
  };

  // ==========================
  //  ROOM STATUS
  // ==========================
  const [roomStatus, setRoomStatus] = useState({
    ready: 0,
    dirty: 0,
    cleaning: 0,
    outOfService: 0,
    occupied: 0,
    total: 0,
  });

  // ==========================
  //  STAFF SHIFTS
  // ==========================
  const [staffSummary, setStaffSummary] = useState<any>(null);
  const [staffLoading, setStaffLoading] = useState(true);

  useEffect(() => {
    async function loadStaff() {
      try {
        const res = await axios.get(`${API_BASE}/api/staff-attendance/today-summary`, {
          headers: authHeaders,
        });
        setStaffSummary(res.data || null);
      } catch (err) {
        console.error("Staff Load Error:", err);
      } finally {
        setStaffLoading(false);
      }
    }
    loadStaff();
  }, []);

  // ==========================
  //  KPI STATE
  // ==========================
  const [kpis, setKpis] = useState({
    occupancy: { percent: 0, booked: 0, total: 0 },
    revenueMonth: { total: 0, nights: 0 },
    revenueTotal: 0,
    bookings: { total: 0, active: 0, month: 0 },
    availableRooms: { total: 0, ready: 0, cleaning: 0 },

    ADR: 0,
    RevPAR: 0,

    forecast7: [],
    guestsInHouse: { total: 0, checkoutsRemaining: 0 },

    bookingPace: 0,
    cancellations30: 0,
    leadTime: 0,
  });

  // Pending payment / on-hold rooms summary
  const [pendingSummary, setPendingSummary] = useState({ pendingPayment: 0, onHoldRooms: 0 });

  // ==========================
  //  CHARTS
  // ==========================
  const [charts, setCharts] = useState({
    revenue30: [],
    occupancyTrend: [],
    bookingPace: [],
    cancellations30: [],
    leadTime: [],
    revenueByType: {},
  });

  // ==========================
  //  ARRIVALS & DEPARTURES
  // ==========================
  const [traffic, setTraffic] = useState({
    arrivalsToday: [],
    departuresToday: [],
  });

  // ==========================
  //  MAIN DASHBOARD LOADER
  // ==========================
  useEffect(() => {
    async function loadEverything() {
      try {

        // 1 — KPIs
        const kpiRes = await axios.get(`${API_BASE}/api/dashboard/kpis`, {
          headers: authHeaders,
        });

        // 2 — Charts
        const chartRes = await axios.get(`${API_BASE}/api/dashboard/charts`, {
          headers: authHeaders,
        });

        // 3 — Arrivals + Departures
        const trafficRes = await axios.get(
          `${API_BASE}/api/dashboard/arrivals-departures`,
          { headers: authHeaders }
        );

        // 4 — Room Status
        const statusRes = await axios.get(
          `${API_BASE}/api/dashboard/room-status`,
          { headers: authHeaders }
        );

        // 5 — Pending Summary (pending payment + on-hold rooms)
        let pendingRes = { data: { pendingPayment: 0, onHoldRooms: 0 } };
        try {
          pendingRes = await axios.get(`${API_BASE}/api/dashboard/pending-summary`, {
            headers: authHeaders,
          });
        } catch { /* non-critical */ }

        // Apply all results
        setKpis((prev) => ({ ...prev, ...kpiRes.data }));
        setCharts(chartRes.data);
        setTraffic(trafficRes.data);
        setRoomStatus(statusRes.data);
        setPendingSummary(pendingRes.data);

        setLoading(false);
      } catch (err) {
        console.error("Dashboard Load Error:", err);
        setLoading(false);
      }
    }

    loadEverything();
  }, []);

  return (
    <DashboardLayout>
      <ManagerHeader />

      <KPICards
        loading={loading}
        occupancy={kpis.occupancy}
        revenueMonth={kpis.revenueMonth}
        revenueTotal={kpis.revenueTotal}
        bookings={kpis.bookings}
        availableRooms={kpis.availableRooms}
        ADR={kpis.ADR}
        RevPAR={kpis.RevPAR}
        forecast7={kpis.forecast7}
        guestsInHouse={kpis.guestsInHouse}
        bookingPace={kpis.bookingPace}
        cancellations30={kpis.cancellations30}
        leadTime={kpis.leadTime}
        pendingPayment={pendingSummary.pendingPayment}
        onHoldRooms={pendingSummary.onHoldRooms}
      />

      <ChartsSection
        revenue30={charts.revenue30}
        occupancyTrend={charts.occupancyTrend}
        bookingPace={charts.bookingPace}
        cancellations30={charts.cancellations30}
        leadTime={charts.leadTime}
        revenueByType={charts.revenueByType}
      />

      <ArrivalsDepartures
        arrivalsToday={traffic.arrivalsToday}
        departuresToday={traffic.departuresToday}
      />

      <RoomStatusBar roomStatus={roomStatus} />

      <StaffShifts summary={staffSummary} loading={staffLoading} />
    </DashboardLayout>
  );
}
